import Dexie from 'dexie';
import moment from 'moment';
import {getUsername} from '../../_shared/GWTBridge/invoke/genPage';
import {isPersonalizationSource} from '../../_shared/GWTBridge/invoke/netForm';
import {
    getNeverShowAgainByForm,
    getNeverShowAgainServer,
    checkClearStorage
} from '../../features/Personalization/actions';
import {
    getLastCollectionDates,
    getPrevCollectionDate,
    startNewCollection
} from '../../features/Personalization/service/indexDBService';

import {configuredDate} from '../../features/Personalization/utils/personalization';
import {configuration as baseConfiguration} from '../../features/Personalization/config';

interface DataItem {
    date: string;
    count: number;
}
interface SubForm {
    name: string;
    parentName: string;
    user: string;
    collectionDate?: string;
    title: string;
    data?: DataItem[];
}

interface CollectionDate {
    date: string;
    id?: number;
    neverShowAgain: boolean;
    formName: string;
    user: string;
}

interface BlackList {
    name: string;
    user: string;
}

class PersonalizationSubForms extends Dexie {
    public subForms: Dexie.Table<SubForm, string>;
    public collectionDates: Dexie.Table<CollectionDate, number>;
    public blackList: Dexie.Table<BlackList, number>;

    constructor() {
        super('PersonalizationSubForms');
        this.version(1).stores({
            subForms: '[name+collectionDate+user+parentName],[collectionDate+user+parentName]',
            collectionDates: '[formName+date+user], [formName+user], user',
            blackList: 'user, name'
        });
    }
}

export const db = new PersonalizationSubForms();

export const configuration = {
    ...baseConfiguration,
    minSubFormsEntries: 1,
    neverFormKey: 'neverShowSubForm',
    neverAllKey: 'neverShowSubFormsSugg'
};

export const addNewSubFormEntry = async (subForm: SubForm) => {
    db.transaction('rw', db.subForms, db.collectionDates, db.blackList, async () => {
        const userName = getUsername();

        const blackList = await db.blackList.where('user').equals(userName).toArray();

        if (blackList.some(blItem => blItem.name === subForm.parentName)) {
            return;
        }
        const lastCollection = await getLastCollectionDates(db, subForm.parentName);

        const collectionDate =
            lastCollection?.date ||
            (await startNewCollection(db, subForm.parentName, configuration));

        if (!lastCollection?.neverShowAgain) {
            const today = configuredDate().format('DD/MM/YYYY');

            const entityToUpdate = await db.subForms.get({
                name: subForm.name,
                collectionDate,
                user: userName,
                parentName: subForm.parentName
            });
            if (entityToUpdate) {
                db.subForms
                    .where('[name+collectionDate+user+parentName]')
                    .equals([
                        entityToUpdate.name,
                        collectionDate,
                        entityToUpdate.user,
                        entityToUpdate.parentName
                    ])
                    .modify(ent => {
                        const todayEntryIndex = ent.data.findIndex(data => data.date === today);
                        if (todayEntryIndex > -1) {
                            ent.data[todayEntryIndex].count = ent.data[todayEntryIndex].count + 1;
                        } else {
                            ent.data.push({date: today, count: 1});
                        }
                    });
            } else {
                db.subForms.add({
                    ...subForm,
                    collectionDate,
                    user: userName,
                    data: [{date: today, count: 1}]
                });
            }
        }
    }).catch(e => {
        console.log('error adding new entry  entitys', e);
    });
};

const isNeverShowAgainMap = {};
const isNeverShowAgainFormMap = {};

const getNeverShowAgain = (formName: string) => {
    return isNeverShowAgainMap[formName];
};

const getNeverShowAgainForm = (formName: string) => {
    return isNeverShowAgainFormMap[formName];
};

export const getSubFormsSuggestion = async (parentForm, subforms, isSnoozed) => {
    if (isPersonalizationSource()) {
        const userName = getUsername();
        let lastCollectionDate, shouldExecute;
        let subFormsMonthly = [];
        let neverShowAgain = false;

        checkClearStorage();

        let isNeverShowAgainForm = getNeverShowAgain(parentForm);
        let isNeverShowAgainAll = getNeverShowAgainForm(parentForm);

        if (!(parentForm in isNeverShowAgainFormMap)) {
            isNeverShowAgainForm = await getNeverShowAgainByForm(
                parentForm,
                configuration.neverFormKey
            );
            isNeverShowAgainFormMap[parentForm] = isNeverShowAgainForm;
        }
        if (!(parentForm in isNeverShowAgainMap)) {
            isNeverShowAgainAll = await getNeverShowAgainServer(configuration.neverAllKey);
            isNeverShowAgainMap[parentForm] = isNeverShowAgainAll;
        }

        neverShowAgain = isNeverShowAgainAll || isNeverShowAgainForm;

        if (!neverShowAgain) {
            await db
                .transaction('r', db.collectionDates, db.subForms, db.blackList, async () => {
                    //check parent form in black list
                    const blackList = await db.blackList.where('user').equals(userName).toArray();

                    if (blackList.some(blItem => blItem.name === parentForm)) {
                        return;
                    }

                    const collectionDate = isSnoozed
                        ? await getPrevCollectionDate(db, parentForm)
                        : await getLastCollectionDates(db, parentForm);

                    neverShowAgain = collectionDate?.neverShowAgain;
                    if (!collectionDate || neverShowAgain) {
                        return;
                    }

                    lastCollectionDate = moment(collectionDate?.date, 'DD/MM/YYYY');
                    const daysDiff = configuredDate().diff(lastCollectionDate, 'days');

                    shouldExecute = isSnoozed || daysDiff >= configuration.days;

                    if (shouldExecute) {
                        subFormsMonthly = await db.subForms
                            .where('[collectionDate+user+parentName]')
                            .equals([collectionDate.date, userName, parentForm])
                            .toArray();
                        if (subFormsMonthly.length > 0) {
                            return;
                        }
                    }
                })
                .catch(e => console.log('get data from indexedDB error: ', e));

            if (
                configuration.minSubFormsEntries <= subFormsMonthly.length &&
                !neverShowAgain &&
                shouldExecute
            ) {
                const subFormsEntries = calcEntitiesToAdd(subFormsMonthly, subforms);

                if (subFormsEntries?.length === 0) {
                    await startNewCollection(db, parentForm, configuration);
                }
                return subFormsEntries;
            }
        }
    }
    return [];
};

const calcEntitiesToAdd = (usedSubForms: SubForm[], subForms: any[]) => {
    const arr = usedSubForms.map(({name}) => {
        return name;
    });

    return subForms.slice(1).filter(({name}) => {
        return !arr.includes(name);
    });
};
